// Bulk upload queue + floating progress panel.
//
// Drops the single-file pattern in favor of a queue of arbitrary size.
// Each file uploads independently via the existing /documents/upload
// endpoint — no server-side bulk route needed. The queue throttles to a
// configurable concurrency (default 3) so the API isn't flooded by a
// 200-file drop.
//
// API surface:
//
//   const upload = useBulkUpload({
//     onSettled: () => qc.invalidateQueries({ queryKey: ['documents'] }),
//   });
//
//   upload.enqueue(files);                  // FileList | File[]
//   upload.queue                            // current items + status
//   upload.activeCount                      // in-flight
//   upload.dismiss()                        // clear finished items
//   <BulkUploadPanel queue={upload.queue} ... />
//
// Status lifecycle per item:
//   queued  →  uploading  →  done    (success)
//                        →  error   (HTTP / network / validation)

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DOCUMENT_BULK_EDIT_MAX_IDS,
  normalizeGroupLabel,
  type DocumentBulkEditResponse,
  type DocumentListItem,
  type FileTask,
} from '@captain-tasko/shared';
import { apiSingle, apiUpload } from '../lib/api.js';
import { useDocumentGrouping } from '../hooks/useDocumentGrouping.js';
import { batchIdForAction, batchIdForDirectoryDrop, uploadFields } from '../lib/ingest-batch.js';
import type { DroppedFile } from '../lib/dropped-entries.js';
import { toast } from './Toast.js';

export type BulkUploadStatus = 'queued' | 'uploading' | 'done' | 'error';

export interface BulkUploadItem {
  id: string;
  file: File;
  status: BulkUploadStatus;
  error?: string;
  resultId?: string;
  /** CTSK-500 — shared by every file of ONE enqueue() call (multi-file + grouping on); decided once at enqueue time. */
  batchId?: string | null;
  /** CTSK-501 — relative folder (root name included) for files of a directory drop; absent otherwise. */
  sourceFolder?: string | null;
  /** CTSK-501 — which enqueue call this item belongs to (one directory drop = one action). */
  actionId?: number;
}

/** CTSK-501 — what a directory drop leaves out, reported once the drop has finished uploading. */
export interface DirectoryDropReport {
  unsupported: number;
  unsupportedNames: string[];
  overCap: number;
}

export interface EnqueueDirectoryOptions {
  /** Group to apply to the drop's documents once every file has settled (existing bulk-edit path). */
  groupName?: string | null;
  report?: DirectoryDropReport;
}

interface UseBulkUploadOptions {
  /** Max in-flight uploads. Default 3 — high enough to feel snappy on
   *  small files, low enough that a 50MB markdown vault won't saturate
   *  upstream. */
  concurrency?: number;
  /** Fired once the queue drains (every item is `done` or `error`).
   *  The list view typically invalidates its query here. */
  onSettled?: (summary: { ok: number; failed: number }) => void;
  /** CTSK-501 — fired after an auto-named group was applied (or attempted), so the list can refetch. */
  onGroupApplied?: () => void;
}

let nextId = 0;
let nextActionId = 0;

export function useBulkUpload({ concurrency = 3, onSettled, onGroupApplied }: UseBulkUploadOptions = {}) {
  const [queue, setQueue] = useState<BulkUploadItem[]>([]);
  // CTSK-500 — same gate the Bulk Edit button uses (env flag + org setting).
  // Held in a ref so enqueue() keeps a stable identity.
  const { enabled: groupingEnabled } = useDocumentGrouping();
  const groupingRef = useRef(groupingEnabled);
  groupingRef.current = groupingEnabled;
  const activeRef = useRef(0);
  // Hold the latest queue in a ref so the drain loop sees fresh values
  // without React state-staleness on rapid enqueues.
  const queueRef = useRef<BulkUploadItem[]>([]);
  queueRef.current = queue;

  const updateItem = useCallback((id: string, patch: Partial<BulkUploadItem>) => {
    setQueue((q) => q.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  // Drain loop: while we have capacity AND queued items, kick the next
  // one off. Reentrant — safe to call on enqueue, on completion, etc.
  //
  // IMPORTANT (#114): updateItem schedules an async setState, so within
  // the same drain tick `queueRef.current` does NOT reflect the
  // status='uploading' change until React commits. Without the in-place
  // ref advance below, the while loop would pick the SAME 'queued' item
  // up to `concurrency` times before React flushes — kicking off N
  // duplicate uploads of one file. The optimistic ref update keeps the
  // loop's view of the queue consistent with what's already in-flight.
  const drain = useCallback(() => {
    while (activeRef.current < concurrency) {
      const next = queueRef.current.find((it) => it.status === 'queued');
      if (!next) break;
      activeRef.current += 1;
      // Advance the ref synchronously so the next iteration doesn't
      // re-pick this item. The setState below updates the visible
      // queue once React flushes — both pathways converge on the same
      // 'uploading' state.
      queueRef.current = queueRef.current.map((it) =>
        it.id === next.id ? { ...it, status: 'uploading' as const } : it,
      );
      updateItem(next.id, { status: 'uploading' });

      apiUpload<{ document: DocumentListItem; fileTask: FileTask }>(
        '/documents/upload',
        next.file,
        uploadFields(next.batchId, next.sourceFolder),
      )
        .then((res) => {
          updateItem(next.id, { status: 'done', resultId: res.document.id });
        })
        .catch((err: Error) => {
          updateItem(next.id, { status: 'error', error: err.message || 'Upload failed' });
        })
        .finally(() => {
          activeRef.current -= 1;
          // Tail call to keep draining. Defer so the state update from
          // updateItem above flushes before we look for the next queued.
          setTimeout(drain, 0);
        });
    }
  }, [concurrency, updateItem]);

  // Fire onSettled exactly once when nothing's queued or running.
  const settledRef = useRef(true);
  useEffect(() => {
    const anyActive = queue.some((it) => it.status === 'queued' || it.status === 'uploading');
    if (anyActive) {
      settledRef.current = false;
      return;
    }
    if (queue.length > 0 && !settledRef.current) {
      settledRef.current = true;
      const ok = queue.filter((it) => it.status === 'done').length;
      const failed = queue.filter((it) => it.status === 'error').length;
      onSettled?.({ ok, failed });
    }
  }, [queue, onSettled]);

  // CTSK-501 — per directory drop: the group to apply and the report to show
  // once every file of THAT drop has settled (other drops may still be running).
  const pendingActionsRef = useRef(new Map<number, { groupName: string | null; report?: DirectoryDropReport }>());
  const onGroupAppliedRef = useRef(onGroupApplied);
  onGroupAppliedRef.current = onGroupApplied;

  useEffect(() => {
    const pending = pendingActionsRef.current;
    if (pending.size === 0) return;
    for (const [actionId, info] of [...pending.entries()]) {
      const items = queue.filter((it) => it.actionId === actionId);
      if (items.some((it) => it.status === 'queued' || it.status === 'uploading')) continue;
      pending.delete(actionId);
      const ids = items.filter((it) => it.status === 'done' && it.resultId).map((it) => it.resultId!);
      const failed = items.filter((it) => it.status === 'error').length;
      if (info.report && (info.report.unsupported > 0 || info.report.overCap > 0 || failed > 0)) {
        const parts: string[] = [];
        if (info.report.unsupported > 0) {
          const names = info.report.unsupportedNames.slice(0, 5).join(', ');
          parts.push(`${info.report.unsupported} unsupported file${info.report.unsupported === 1 ? '' : 's'} skipped${names ? ` (${names}${info.report.unsupported > 5 ? ', …' : ''})` : ''}`);
        }
        if (info.report.overCap > 0) parts.push(`${info.report.overCap} over the per-drop limit not uploaded`);
        if (failed > 0) parts.push(`${failed} failed`);
        toast.info(`Folder upload: ${parts.join('; ')}.`);
      }
      if (info.groupName && ids.length > 0) void assignGroup(ids, info.groupName, onGroupAppliedRef);
    }
  }, [queue]);

  const enqueue = useCallback(
    (input: FileList | File[]) => {
      const files = Array.from(input);
      if (files.length === 0) return;
      // CTSK-500 — ONE id per enqueue() call (= one drop / picker action),
      // minted here and stored on each item, so the 3-at-a-time drain order
      // cannot change it. Single file or grouping off → null.
      const batchId = batchIdForAction(files.length, groupingRef.current);
      const items: BulkUploadItem[] = files.map((file) => ({
        id: `bulk-${++nextId}`,
        file,
        status: 'queued',
        batchId,
      }));
      setQueue((q) => [...q, ...items]);
      // Re-grab the ref synchronously then start draining; the new
      // items will be in queueRef.current after React flushes, but the
      // setTimeout(0) handoff inside drain handles late visibility.
      setTimeout(drain, 0);
    },
    [drain],
  );

  // CTSK-501 — one DIRECTORY drop / folder pick. The batch id is minted here
  // once and ALWAYS (even for a single file — a folder is itself the grouping
  // signal), and every file carries its relative sourceFolder. With grouping
  // off nothing but the file is sent: the files upload flat, exactly like a
  // plain multi-file drop would without the feature.
  const enqueueDirectory = useCallback(
    (dropped: DroppedFile[], options: EnqueueDirectoryOptions = {}) => {
      if (dropped.length === 0) return;
      const on = groupingRef.current;
      const batchId = batchIdForDirectoryDrop(dropped.length, on);
      const actionId = ++nextActionId;
      const items: BulkUploadItem[] = dropped.map(({ file, sourceFolder }) => ({
        id: `bulk-${++nextId}`,
        file,
        status: 'queued',
        batchId,
        sourceFolder: on ? sourceFolder : null,
        actionId,
      }));
      const label = on && options.groupName ? normalizeGroupLabel(options.groupName) : null;
      const groupName = label && label.ok ? label.value : null;
      if (groupName || options.report) pendingActionsRef.current.set(actionId, { groupName, report: options.report });
      setQueue((q) => [...q, ...items]);
      setTimeout(drain, 0);
    },
    [drain],
  );

  const dismiss = useCallback(() => {
    // Drop items that are settled. Keeps in-flight + queued so the user
    // can dismiss a partial run without canceling work.
    setQueue((q) => q.filter((it) => it.status === 'queued' || it.status === 'uploading'));
  }, []);

  const reset = useCallback(() => {
    // Hard clear — finishes settled list AND any queued items. In-flight
    // requests still complete (we can't actually cancel mid-fetch
    // without an AbortSignal plumbed through apiUpload).
    setQueue((q) => q.filter((it) => it.status === 'uploading'));
  }, []);

  return {
    queue,
    activeCount: queue.filter((it) => it.status === 'uploading').length,
    queuedCount: queue.filter((it) => it.status === 'queued').length,
    doneCount: queue.filter((it) => it.status === 'done').length,
    errorCount: queue.filter((it) => it.status === 'error').length,
    enqueue,
    enqueueDirectory,
    dismiss,
    reset,
  };
}

/**
 * CTSK-501 — apply the auto-named group to the documents a directory drop just
 * created, through the existing Stage 1 bulk-edit path (POST /documents/bulk-edit,
 * REPLACE groupName). No new endpoint. Chunked to the endpoint's id cap; a
 * failure is reported but never undoes the upload.
 */
async function assignGroup(
  ids: string[],
  groupName: string,
  onApplied: { current: (() => void) | undefined },
): Promise<void> {
  let ok = 0;
  let failed = 0;
  for (let i = 0; i < ids.length; i += DOCUMENT_BULK_EDIT_MAX_IDS) {
    const chunk = ids.slice(i, i + DOCUMENT_BULK_EDIT_MAX_IDS);
    try {
      const res = await apiSingle<DocumentBulkEditResponse>('/documents/bulk-edit', {
        method: 'POST',
        body: { ids: chunk, mutations: { groupName: { strategy: 'REPLACE', value: groupName } } },
      });
      ok += res.summary.succeeded;
      failed += res.summary.failed + res.summary.skipped;
    } catch {
      failed += chunk.length;
    }
  }
  if (failed === 0) toast.success(`Group “${groupName}” applied to ${ok} document${ok === 1 ? '' : 's'}.`);
  else toast.error(`Group “${groupName}”: ${ok} updated, ${failed} failed.`);
  onApplied.current?.();
}

// ─── UI panel ─────────────────────────────────────────────────────────────

export function BulkUploadPanel({
  queue,
  activeCount,
  queuedCount,
  doneCount,
  errorCount,
  onDismiss,
}: {
  queue: BulkUploadItem[];
  activeCount: number;
  queuedCount: number;
  doneCount: number;
  errorCount: number;
  onDismiss: () => void;
}) {
  if (queue.length === 0) return null;

  const total = queue.length;
  const settled = doneCount + errorCount;
  const allDone = activeCount === 0 && queuedCount === 0;
  const pctDone = total > 0 ? Math.round((settled / total) * 100) : 0;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        width: 360,
        maxHeight: '60vh',
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-strong)',
        borderRadius: 'var(--radius-md)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <header
        style={{
          padding: '10px 14px',
          borderBottom: '1px solid var(--border-subtle)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <strong style={{ fontSize: 14 }}>
            {allDone ? 'Upload complete' : `Uploading ${activeCount}/${total}`}
          </strong>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
            {doneCount} done · {errorCount} failed · {queuedCount} queued
          </div>
        </div>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={onDismiss}
          aria-label="Dismiss"
          title={allDone ? 'Clear' : 'Hide finished items'}
        >
          ×
        </button>
      </header>

      {/* progress bar */}
      <div style={{ padding: '8px 14px 0' }}>
        <div
          style={{
            height: 4,
            background: 'var(--surface-sunken)',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${pctDone}%`,
              height: '100%',
              background: errorCount > 0 ? 'var(--warning-primary, #B86E4D)' : 'var(--accent-primary)',
              transition: 'width 200ms',
            }}
          />
        </div>
      </div>

      <ul
        style={{
          listStyle: 'none',
          margin: 0,
          padding: '8px 14px 12px',
          overflowY: 'auto',
          fontSize: 12,
        }}
      >
        {queue.map((it) => (
          <li
            key={it.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '4px 0',
              borderBottom: '1px solid var(--border-subtle)',
            }}
          >
            <StatusDot status={it.status} />
            <span
              style={{
                flex: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={it.file.name + (it.error ? ` — ${it.error}` : '')}
            >
              {it.file.name}
            </span>
            <span style={{ color: 'var(--text-muted)', fontFeatureSettings: '"tnum"' }}>
              {formatBytes(it.file.size)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatusDot({ status }: { status: BulkUploadStatus }) {
  const color =
    status === 'done' ? '#3C8E7A' :
    status === 'error' ? '#B3261E' :
    status === 'uploading' ? 'var(--accent-primary)' :
    '#8E8B81';
  return (
    <span
      aria-label={status}
      title={status}
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: color,
        flex: '0 0 8px',
        animation: status === 'uploading' ? 'pulse 1.2s infinite' : undefined,
      }}
    />
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
